import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { CustomerViewTimelineStepIcon } from "./customer-view-timeline-step-icon";
import { CustomerViewTimelineProps } from "./customer-view-timeline.props";
import { QontoConnector } from "./customer-view-timeline.constant";
import "./customer-view-timeline.scss";
import { getCustomerDetails } from "../../../../api/customer";
import { formatDateTime } from "../../../../utils/utils";

const CustomerViewTimeline: React.FC<CustomerViewTimelineProps> = (
  props: any,
) => {
  const {
    selectedCustomerId,
    setLoading,
    setUpdatedDate,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
  } = props;
  const [historyDetails, setHistoryDetails] = useState<any>(null);

  const handleEditOpen = () => {};
  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getCustomerDetails(selectedCustomerId);
      setHistoryDetails(response?.data?.data?.customerHistory || []);
      setTimeout(() => setLoading(false), 1000);
      setUpdatedDate(formatDateTime(response?.data?.data?.updated_at));
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCustomerDetails();
  }, [selectedCustomerId]);

  const formatFieldName = (fieldName: string): string => {
    const fieldMap: Record<string, string> = {
      // Customer fields
      first_name: "First Name",
      last_name: "Last Name",
      company: "Company",
      lead_owner: "Customer Owner",
      description: "Description",
      fax_number: "Fax",
      industry: "Industry",
      interested_products: "Interested Products",
      lead_source: "Customer Source",
      lead_status: "Customer Status",
      number_of_employees: "No of employees",
      primary_email_address: "Email",
      primary_phone_number: "Primary Phone Number",
      rating: "Confidence Score",
      secondary_email_addres: "Secondary Email",
      secondary_phone_number: "Secondary Phone Number",
      customer_owner_id: "Customer Owner",
      title: "Title",
      website_url: "Website",
      street_line_1: "Street Line 1",
      street_line_2: "Street Line 2",
      street: "Street",
      city: "City",
      state: "State",
      country: "Country",
      zipcode: "Zipcode",
      age: "Age",
      preferred_contact_mode: "Preferred Contact Mode",
      assistant_phone_number: "Assistant Phone Number",
      lead_value: "Customer Value",
      secondary_email_address: "Secondary Email",
      assistant: "Assistant",
      middle_name: "Middle Name",
      dob: "Date of Birth",

      // Task / Meeting fields
      start_date: "Start Date",
      closed_time: "Closed Time",
      status: "Status",
      type: "Campaign Type",
      location: "Location",
      meetStartTime: "From Date and Time",
      meetEndTime: "To Date and Time",
      host_id: "Host",
      participants: "Participants",
      related_to: "Related To",
      subject: "Subject",
      due_date: "Due Date",
      priority: "Priority",
      task_status: "Task Status",
      reminder: "Reminder",
      owner: "Owner",
      all_day: "All Day",

      // Call fields
      call_owner_id: "Call Owner",
      related_to_campaign_id: "Related To Campaign",
      related_to_lead_id: "Related To Lead",
      outgoing_call_status: "Outgoing Call Status",
      call_start_time: "Call Start Time",
      call_purpose: "Call Purpose",
      call_agenda: "Call Agenda",
      notes: "Notes",
      call_duration: "Call Duration",
      call_results: "Call Results",
      call_type: "Call Type",
      callFor: "Call For",
      owner_id: "Lead Owner",
      cancel_notes: "Cancel Notes",
    };

    return fieldMap[fieldName] || fieldName;
  };
  const renderFormattedDescription = (description: string) => {
    const parts = description.split(/(<b>.*?<\/b>)/g);

    return parts.map((part, index) => {
      if (part.startsWith("<b>") && part.endsWith("</b>")) {
        return <b key={index}>{part.substring(3, part.length - 4)}</b>;
      }
      return <span key={index}>{part}</span>;
    });
  };
  const groupByDate = (
    histories: any[],
  ): Record<string, { label: string; date: string }[]> => {
    return histories.reduce(
      (acc, history) => {
        const date = new Date(history.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = [];
        }

        const createdBy = history.createdBy
          ? `- By ${history.createdBy.first_name} ${history.createdBy.last_name}`
          : "";
        acc[date].push({
          label: (
            <span>
              <span>
                <b>{formatFieldName(history.field_name)} </b>
                {renderFormattedDescription(history.description)}{" "}
              </span>
              <span className="customer-list-view-timeline-info-bar-box-steps-sub">
                {" "}
                {createdBy}
              </span>
            </span>
          ),
          date: formatDateTime(history.created_at),
        });

        return acc;
      },
      {} as Record<string, { label: string; date: string }[]>,
    );
  };

  const steps: Record<string, { label: string; date: string }[]> =
    historyDetails ? groupByDate(historyDetails) : {};

  return (
    <div className="customer-list-view-timeline-info">
      <div className="customer-list-view-timeline-info-header">
        <div className="customer-list-view-timeline-info-header-text">
          Timeline
        </div>
      </div>
      {Object.entries(steps).map(([date, stepData], index) => (
        <div className="customer-list-view-timeline-info-bar-box" key={index}>
          <div className="customer-list-view-timeline-info-bar-box-date">
            {date}
          </div>
          <div className="customer-list-view-timeline-info-bar-box-steps">
            <Box sx={{ width: "100%" }}>
              <Stepper
                activeStep={stepData.length}
                connector={<QontoConnector />}
                orientation="vertical"
                sx={{ gap: 0, p: 0, m: 0 }}
              >
                {stepData.map((data, idx) => (
                  <Step key={idx}>
                    <StepLabel StepIconComponent={CustomerViewTimelineStepIcon}>
                      <div className="customer-list-view-timeline-info-bar-box-steps-main-sub">
                        <div className="customer-list-view-timeline-info-bar-box-steps-main">
                          {data.label}
                        </div>
                        <div className="customer-list-view-timeline-info-bar-box-steps-sub">
                          {data.date}
                        </div>
                      </div>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CustomerViewTimeline;
